import React, { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  TextField,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  Chip,
  InputAdornment,
  Divider,
} from "@mui/material";
import {
  Search as SearchIcon,
  ShoppingBag,
  Mail,
  Settings,
  AttachMoney,
  ShoppingCart,
  Extension,
  Memory,
  ManageAccounts,
  History as HistoryIcon,
  KeyboardArrowDown,
  KeyboardReturn,
} from "@mui/icons-material";
import "./CommandPalette.css";

/**
 * CommandPalette - Paleta de comandos para navegación rápida en AdminConsole
 * Atajos de teclado: Ctrl+K (Windows/Linux) o Cmd+K (Mac)
 * 
 * Características:
 * - Búsqueda fuzzy en todas las páginas disponibles
 * - Navegación con teclado (flechas arriba/abajo, Enter)
 * - Historial de páginas recientes
 * - Integración con Material-UI
 */

const CommandPalette = ({ open, onClose }) => {
  const history = useHistory();
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [recentPages, setRecentPages] = useState([]);
  const inputRef = useRef(null);

  // Definir todas las páginas disponibles en la AdminConsole
  const allPages = [
    {
      id: "coupons",
      title: "Coupons",
      path: "/coupons",
      description: "Gestión de cupones y descuentos",
      icon: <ShoppingBag />,
      category: "principal",
    },
    {
      id: "contact",
      title: "Contact",
      path: "/contact",
      description: "Mensajes y contactos",
      icon: <Mail />,
      category: "principal",
    },
    {
      id: "settings",
      title: "Settings",
      path: "/settings",
      description: "Configuración del sistema",
      icon: <Settings />,
      category: "configuración",
    },
    {
      id: "credit-package",
      title: "Credit Package",
      path: "/credit-package",
      description: "Gestión de créditos",
      icon: <AttachMoney />,
      category: "facturación",
    },
    {
      id: "storage",
      title: "Storage",
      path: "/storage",
      description: "Planes de almacenamiento",
      icon: <ShoppingCart />,
      category: "facturación",
    },
    {
      id: "extensions",
      title: "Extensions",
      path: "/extensions",
      description: "Extensiones del sistema",
      icon: <Extension />,
      category: "herramientas",
    },
    {
      id: "ai-studio",
      title: "AI Studio",
      path: "/ai-studio",
      description: "Gestión de AI Studio",
      icon: <Memory />,
      category: "herramientas",
    },
    {
      id: "plan-control",
      title: "Plan Control",
      path: "/PlanControl",
      description: "Control de planes y suscripciones",
      icon: <ManageAccounts />,
      category: "facturación",
    },
  ];

  // Cargar páginas recientes del localStorage
  useEffect(() => {
    const stored = localStorage.getItem("adminConsoleRecentPages");
    if (stored) {
      try {
        setRecentPages(JSON.parse(stored));
      } catch (e) {
        console.error("Error loading recent pages:", e);
      }
    }
  }, []);

  // Guardar página visitada en el historial
  const saveRecentPage = (pageId) => {
    const newRecent = [pageId, ...recentPages.filter((id) => id !== pageId)].slice(0, 5);
    setRecentPages(newRecent);
    localStorage.setItem("adminConsoleRecentPages", JSON.stringify(newRecent));
  };

  // Filtrar páginas según la búsqueda (fuzzy search)
  const filteredPages = query.trim()
    ? allPages.filter(
        (page) =>
          page.title.toLowerCase().includes(query.toLowerCase()) ||
          page.description.toLowerCase().includes(query.toLowerCase()) ||
          page.category.toLowerCase().includes(query.toLowerCase())
      )
    : allPages;

  // Obtener páginas recientes
  const getRecentPages = () => {
    return recentPages
      .map((id) => allPages.find((page) => page.id === id))
      .filter(Boolean)
      .slice(0, 3);
  };

  // Navegar a una página
  const navigateToPage = (page) => {
    if (page) {
      saveRecentPage(page.id);
      history.push(page.path);
      handleClose();
    }
  };

  // Manejar cierre del diálogo
  const handleClose = () => {
    setQuery("");
    setSelectedIndex(0);
    onClose();
  };

  // Navegación con teclado
  const handleKeyDown = (e) => {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) => Math.min(prev + 1, filteredPages.length - 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, 0));
        break;
      case "Enter":
        e.preventDefault();
        if (filteredPages[selectedIndex]) {
          navigateToPage(filteredPages[selectedIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        handleClose();
        break;
      default:
        break;
    }
  };

  // Enfocar el input cuando se abre el diálogo
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [open]);

  // Reset cuando se cierra
  useEffect(() => {
    if (!open) {
      setQuery("");
      setSelectedIndex(0);
    }
  }, [open]);

  const recentPagesToShow = getRecentPages();

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: "70vh",
          overflow: "hidden",
        },
      }}
      sx={{
        "& .MuiBackdrop-root": {
          backgroundColor: "rgba(0, 0, 0, 0.7)",
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        {/* Campo de búsqueda */}
        <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
          <TextField
            fullWidth
            autoFocus
            inputRef={inputRef}
            placeholder="Buscar páginas, configuración, herramientas..."
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelectedIndex(0);
            }}
            onKeyDown={handleKeyDown}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "text.secondary" }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <Chip label="ESC" size="small" sx={{ height: 24 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  border: "none",
                },
              },
              "& input": {
                fontSize: "1.1rem",
                color: 'text.primary',
              },
            }}
          />
        </Box>

        {/* Resultados */}
        <Box sx={{ maxHeight: "calc(70vh - 120px)", overflowY: "auto" }}>
          {/* Páginas recientes */}
          {!query && recentPagesToShow.length > 0 && (
            <Box sx={{ py: 1 }}>
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <HistoryIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                <Typography
                  variant="caption"
                  sx={{
                    textTransform: "uppercase",
                    fontWeight: 600,
                    color: "text.secondary",
                    letterSpacing: 0.5,
                  }}
                >
                  Recientes
                </Typography>
              </Box>
              <List dense sx={{ py: 0 }}>
                {recentPagesToShow.map((page, index) => (
                  <ListItem
                    key={page.id}
                    button
                    selected={selectedIndex === index && !query}
                    onClick={() => navigateToPage(page)}
                    sx={{
                      px: 2,
                      py: 1.5,
                      "&.Mui-selected": {
                        backgroundColor: "action.selected",
                      },
                      "&:hover": {
                        backgroundColor: "action.hover",
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 44 }}>
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: 2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "#ffffff",
                          border: "1px solid rgba(0,0,0,0.08)",
                          boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
                          color: "primary.main",
                        }}
                      >
                        {page.icon}
                      </Box>
                    </ListItemIcon>
                    <ListItemText
                      primary={page.title}
                      secondary={page.description}
                      primaryTypographyProps={{
                        fontWeight: 600,
                        color: 'text.primary',
                      }}
                      secondaryTypographyProps={{
                        variant: "body2",
                        color: 'text.secondary',
                      }}
                    />
                  </ListItem>
                ))}
              </List>
              <Divider sx={{ my: 1 }} />
            </Box>
          )}

          {/* Todas las páginas / Resultados de búsqueda */}
          <Box sx={{ py: 1 }}>
            {query && (
              <Box sx={{ px: 2, py: 1 }}>
                <Typography
                  variant="caption"
                  sx={{
                    textTransform: "uppercase",
                    fontWeight: 600,
                    color: "text.secondary",
                    letterSpacing: 0.5,
                  }}
                >
                  Resultados ({filteredPages.length})
                </Typography>
              </Box>
            )}
            <List dense sx={{ py: 0 }}>
              {filteredPages.map((page, index) => (
                <ListItem
                  key={page.id}
                  button
                  selected={selectedIndex === index}
                  onClick={() => navigateToPage(page)}
                  sx={{
                    px: 2,
                    py: 1.5,
                    "&.Mui-selected": {
                      backgroundColor: "action.selected",
                    },
                    "&:hover": {
                      backgroundColor: "action.hover",
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 44 }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#ffffff",
                        border: "1px solid rgba(0,0,0,0.08)",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
                        color: "primary.main",
                      }}
                    >
                      {page.icon}
                    </Box>
                  </ListItemIcon>
                  <ListItemText
                    primary={page.title}
                    secondary={page.description}
                    primaryTypographyProps={{
                      fontWeight: 600,
                      color: 'text.primary',
                    }}
                    secondaryTypographyProps={{
                      variant: "body2",
                      color: 'text.secondary',
                    }}
                  />
                  <Chip
                    label={page.category}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: "0.7rem",
                      mr: 1,
                      bgcolor: '#ffffff',
                      color: 'text.primary',
                      border: '1px solid rgba(0,0,0,0.08)',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                      fontWeight: 600,
                    }}
                  />
                  {selectedIndex === index && (
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: '#ffffff',
                        border: '1px solid rgba(0,0,0,0.08)',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                      }}
                    >
                      <KeyboardReturn sx={{ fontSize: 16, color: 'text.secondary' }} />
                    </Box>
                  )}
                </ListItem>
              ))}
            </List>

            {filteredPages.length === 0 && query && (
              <Box sx={{ p: 4, textAlign: "center" }}>
                <Typography variant="body1" color="text.secondary" gutterBottom>
                  No se encontraron resultados para "{query}"
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Intenta buscar por nombre de página o categoría
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* Footer con atajos de teclado */}
        <Box
          sx={{
            borderTop: 1,
            borderColor: "divider",
            px: 2,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            backgroundColor: "action.hover",
          }}
        >
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: '#ffffff',
                  border: '1px solid rgba(0,0,0,0.08)',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                }}
              >
                <KeyboardArrowDown sx={{ fontSize: 14, color: 'text.secondary' }} />
              </Box>
              <Typography variant="caption" color="text.secondary">
                Navegar
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: '#ffffff',
                  border: '1px solid rgba(0,0,0,0.08)',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                }}
              >
                <KeyboardReturn sx={{ fontSize: 14, color: 'text.secondary' }} />
              </Box>
              <Typography variant="caption" color="text.secondary">
                Seleccionar
              </Typography>
            </Box>
          </Box>
          <Typography variant="caption" color="text.secondary">
            Presiona ESC para cerrar
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default CommandPalette;
